import React, {ChangeEvent, useEffect, useMemo, useState} from "react"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Paper from "@mui/material/Paper"
import Button from "@mui/material/Button"
import dayjs, {type Dayjs} from "dayjs"
import {getTemporadas} from "@/api/properties"

type SeasonKey = "baja" | "media" | "alta"

type SeasonRange = {
   id: string
   start: Dayjs
   end: Dayjs
   type: Exclude<SeasonKey, "baja">
}

interface AppliedDateRange {
   from: Dayjs | null
   to: Dayjs | null
}

interface SeasonSummaryRow {
   key: string
   label: string
   nights: number
   net: number
   owner: number
   cad: number
}

interface SummaryProps {
   property: any
}

const OWNER_SHARE = 0.85
const CAD_SHARE = 0.15
const SUMMARY_START = dayjs("2020-01-01").startOf("day")
const SEASON_LABELS: Record<SeasonKey, string> = {
   baja: "Temporada baja",
   media: "Temporada media",
   alta: "Temporada alta",
}

const toDayjsOrNull = (timestamp: any): Dayjs | null => {
   if (!timestamp) return null
   if (dayjs.isDayjs(timestamp)) return timestamp
   if (timestamp instanceof Date) return dayjs(timestamp)
   if (typeof timestamp === "number") return dayjs.unix(timestamp)
   if (typeof timestamp === "string") return dayjs(timestamp)
   if (typeof timestamp.seconds === "number") return dayjs.unix(timestamp.seconds)
   if (typeof timestamp._seconds === "number") return dayjs.unix(timestamp._seconds)
   return null
}

const normalizeTemporadas = (data: any[] = []): SeasonRange[] =>
   data
      .map((item) => {
         const start = toDayjsOrNull(item?.initial)?.startOf("day")
         const end = toDayjsOrNull(item?.end)?.startOf("day")
         if (!start || !end) return null
         return {
            id: item.id,
            start,
            end,
            type: item?.is_high ? "alta" : "media",
         }
      })
   .filter(Boolean) as SeasonRange[]

const parseUserDateInput = (value?: string): Dayjs | null => {
   if (!value) return null
   const parsed = dayjs(value)
   return parsed.isValid() ? parsed.startOf("day") : null
}

const buildSeasonLookup = (seasons: SeasonRange[]): Map<string, SeasonKey> => {
   const lookup = new Map<string, SeasonKey>()
   seasons.forEach((season) => {
      let cursor = season.start.startOf("day")
      const endDay = season.end.startOf("day")
      while (cursor.valueOf() <= endDay.valueOf()) {
         lookup.set(cursor.format("YYYY-MM-DD"), season.type)
         cursor = cursor.add(1, "day")
      }
   })
   return lookup
}

const getSeasonFromLookup = (date: Dayjs, lookup: Map<string, SeasonKey>) =>
   lookup.get(date.format("YYYY-MM-DD")) ?? "baja"

const buildSeasonSummary = (
   calendar: any[],
   seasonLookup: Map<string, SeasonKey>,
   costs?: Record<string, number>,
   range?: AppliedDateRange,
): {rows: SeasonSummaryRow[]; totals: SeasonSummaryRow; hasData: boolean} => {
   if (!calendar || calendar.length === 0) {
      const emptyRow: SeasonSummaryRow = {
         key: "totals",
         label: "Noches Totales",
         nights: 0,
         net: 0,
         owner: 0,
         cad: 0,
      }
      return {rows: [], totals: emptyRow, hasData: false}
   }

   // Count nights up to one year into the future
   const summaryEnd = dayjs().add(1, "year").endOf("day")
   const seasonKeys: SeasonKey[] = ["baja", "media", "alta"]
   const totalsBySeason: Record<SeasonKey, number> = {
      baja: 0,
      media: 0,
      alta: 0,
   }

   const rangeStart = range?.from
   const rangeEnd = range?.to

   calendar.forEach((entry) => {
      const start = toDayjsOrNull(entry?.start)?.startOf("day")
      const end = toDayjsOrNull(entry?.end)?.startOf("day")
      if (!start || !end) return

      let current = start
      // Count nights, not days: exclude checkout day (end)
      while (current.valueOf() < end.valueOf()) {
         const withinSummaryWindow =
            current.valueOf() >= SUMMARY_START.valueOf() &&
            current.valueOf() <= summaryEnd.valueOf()
         const withinAppliedRange =
            (!rangeStart || current.valueOf() >= rangeStart.valueOf()) &&
            (!rangeEnd || current.valueOf() <= rangeEnd.valueOf())
         if (withinSummaryWindow && withinAppliedRange) {
            const season = getSeasonFromLookup(current, seasonLookup)
            totalsBySeason[season] += 1
         }
         current = current.add(1, "day")
      }
   })

   const prices: Record<SeasonKey, number> = {
      baja: Number(costs?.noche_regular) || 0,
      media: Number(costs?.noche_media) || 0,
      alta: Number(costs?.noche_alta) || 0,
   }

   const rows: SeasonSummaryRow[] = seasonKeys.map((key) => {
      const nights = totalsBySeason[key]
      const net = nights * prices[key]
      return {
         key,
         label: SEASON_LABELS[key],
         nights,
         net,
         owner: net * OWNER_SHARE,
         cad: net * CAD_SHARE,
      }
   })

   const totals = rows.reduce<SeasonSummaryRow>(
      (acc, row) => ({
         key: "totals",
         label: "Noches Totales",
         nights: acc.nights + row.nights,
         net: acc.net + row.net,
         owner: acc.owner + row.owner,
         cad: acc.cad + row.cad,
      }),
      {key: "totals", label: "Noches Totales", nights: 0, net: 0, owner: 0, cad: 0},
   )

   const hasData = rows.some((row) => row.nights > 0)

   return {rows, totals, hasData}
}

function Summary({property}: SummaryProps) {
   const [seasonRanges, setSeasonRanges] = useState<SeasonRange[]>([])
   const [rangeInput, setRangeInput] = useState<{from: string; to: string}>({
      from: "",
      to: "",
   })
   const [appliedRange, setAppliedRange] = useState<AppliedDateRange>({
      from: null,
      to: null,
   })

   useEffect(() => {
      if (!property?.forRent) {
         setSeasonRanges([])
         return
      }

      let isMounted = true
      const fetchSeasons = async () => {
         try {
            const data = await getTemporadas()
            if (!isMounted) return
            setSeasonRanges(normalizeTemporadas(data))
         } catch (error) {
            console.log("Error de temporadas: " + error)
         }
      }

      fetchSeasons()

      return () => {
         isMounted = false
      }
   }, [property?.forRent])

   const handleRangeChange =
      (field: "from" | "to") => (event: ChangeEvent<HTMLInputElement>) => {
         setRangeInput((prev) => ({...prev, [field]: event.target.value}))
      }

   const handleApplyRange = () => {
      const hasInput = Boolean(rangeInput.from || rangeInput.to)
      if (!hasInput) return
      let startValue: Dayjs | null = parseUserDateInput(rangeInput.from)
      let endValue: Dayjs | null = parseUserDateInput(rangeInput.to)
      if (startValue && endValue && endValue.valueOf() < startValue.valueOf()) {
         const temp = startValue
         startValue = endValue
         endValue = temp
      }
      setAppliedRange({from: startValue, to: endValue})
   }

   const handleClearRange = () => {
      setRangeInput({from: "", to: ""})
      setAppliedRange({from: null, to: null})
   }

   const seasonLookup = useMemo(() => buildSeasonLookup(seasonRanges), [seasonRanges])

   const hasRangeInputs = Boolean(rangeInput.from || rangeInput.to)
   const hasActiveRange = Boolean(appliedRange.from || appliedRange.to)
   const rangeDescription = (() => {
      if (!appliedRange.from && !appliedRange.to) return null
      if (appliedRange.from && appliedRange.to) {
         return `de ${appliedRange.from.format("DD/MM/YYYY")} a ${appliedRange.to.format(
            "DD/MM/YYYY",
         )}`
      }
      if (appliedRange.from) {
         return `desde ${appliedRange.from.format("DD/MM/YYYY")}`
      }
      return `hasta ${appliedRange.to?.format("DD/MM/YYYY")}`
   })()

   const earningsSummary = useMemo(() => {
      if (!property?.forRent || !property?.calendar) return null
      const rangeFilter =
         appliedRange.from || appliedRange.to ? appliedRange : undefined
      return buildSeasonSummary(property.calendar, seasonLookup, property.costos, rangeFilter)
   }, [
      property?.calendar,
      property?.costos,
      property?.forRent,
      seasonLookup,
      appliedRange.from,
      appliedRange.to,
   ])

   const formatCurrency = (value: number) =>
      `${value.toLocaleString("es-MX", {
         minimumFractionDigits: 0,
         maximumFractionDigits: 2,
      })} MXN`

   if (!property?.forRent || !earningsSummary) return null

   return (
      <div className="mt-6">
         <div className="mb-4 space-y-3">
            <div className="flex flex-wrap items-end gap-4">
               <div className="flex flex-col">
                  <label className="text-xs text-gray-500 mb-1">Fecha inicio</label>
                  <input
                     type="date"
                     value={rangeInput.from}
                     onChange={handleRangeChange("from")}
                     className="border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  />
               </div>
               <div className="flex flex-col">
                  <label className="text-xs text-gray-500 mb-1">Fecha fin</label>
                  <input
                     type="date"
                     value={rangeInput.to}
                     onChange={handleRangeChange("to")}
                     className="border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  />
               </div>
               <div className="flex gap-2">
                  <Button
                     variant="contained"
                     size="small"
                     onClick={handleApplyRange}
                     disabled={!hasRangeInputs}
                  >
                     Aplicar fechas
                  </Button>
                  <Button
                     variant="text"
                     size="small"
                     onClick={handleClearRange}
                     disabled={!hasActiveRange}
                  >
                     Limpiar
                  </Button>
               </div>
            </div>
            <div className="text-sm text-center">
               {rangeDescription ? (
                  <p className="text-blue-700">
                     Se muestran el resumen de rentas en el periodo seleccionado{" "}
                     {rangeDescription}.
                  </p>
               ) : (
                  <p className="text-gray-500">
                     Selecciona un rango de fechas para ver los ingresos filtrados.
                  </p>
               )}
            </div>
         </div>
         {earningsSummary.hasData ? (
            <TableContainer component={Paper}>
               <Table aria-label="earnings summary table">
                  <TableHead>
                     <TableRow sx={{backgroundColor: "#00A8F0"}}>
                        <TableCell sx={{color: "white", fontWeight: 600}}>Temporada</TableCell>
                        <TableCell align="center" sx={{color: "white", fontWeight: 600}}>
                           Número de noches
                        </TableCell>
                        <TableCell align="center" sx={{color: "white", fontWeight: 600}}>
                           Ingresos netos
                        </TableCell>
                        <TableCell align="center" sx={{color: "white", fontWeight: 600}}>
                           Ingresos propietario (85%)
                        </TableCell>
                        <TableCell align="center" sx={{color: "white", fontWeight: 600}}>
                           Ingresos CAD (15%)
                        </TableCell>
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {earningsSummary.rows.map((row) => (
                        <TableRow key={row.key}>
                           <TableCell component="th" scope="row">
                              {row.label}
                           </TableCell>
                           <TableCell align="center">{row.nights}</TableCell>
                           <TableCell align="center">{formatCurrency(row.net)}</TableCell>
                           <TableCell align="center">{formatCurrency(row.owner)}</TableCell>
                           <TableCell align="center">{formatCurrency(row.cad)}</TableCell>
                        </TableRow>
                     ))}
                     <TableRow sx={{backgroundColor: "#e5e7eb"}}>
                        <TableCell component="th" scope="row" sx={{fontWeight: 600}}>
                           {earningsSummary.totals.label}
                        </TableCell>
                        <TableCell align="center" sx={{fontWeight: 600}}>
                           {earningsSummary.totals.nights}
                        </TableCell>
                        <TableCell align="center" sx={{fontWeight: 600}}>
                           {formatCurrency(earningsSummary.totals.net)}
                        </TableCell>
                        <TableCell align="center" sx={{fontWeight: 600}}>
                           {formatCurrency(earningsSummary.totals.owner)}
                        </TableCell>
                        <TableCell align="center" sx={{fontWeight: 600}}>
                           {formatCurrency(earningsSummary.totals.cad)}
                        </TableCell>
                     </TableRow>
                  </TableBody>
               </Table>
            </TableContainer>
         ) : (
            <p className="text-center text-gray-500">
               No hay noches registradas desde 2020 para calcular ingresos.
            </p>
         )}
      </div>
   )
}

export default Summary
